import $ from "jquery";
import _ from "lodash";
import config from "config";
import Promise from "bluebird";
import requestTracker from "services/activeRequestTracker";
import embeddingService from "services/embeddingService";
import LOG from "services/loggingService";
import HttpError from "util/HttpError";
import {
    HEADER_GENESYS_APP,
    HEADER_CLIENT_PATH,
    getGenesysAppHeaderValue,
} from "util/apiUtils";

const apiRoot = config.api.uri;
let apiVersion = null;
const REQUEST_TIMEOUT = config.api.timeout;
let oAuthBearerToken;

const queryStr = function (parameters) {
    let query = "";
    const arrayParameterCollection = {};
    let params;

    if (parameters && !_.isEmpty(parameters)) {
        // Get all parameters that are simple key-value pairs and where the value is
        // NOT an array
        params = _.reduce(
            parameters,
            function (result, value, key) {
                if (_.isArray(parameters[key])) {
                    arrayParameterCollection[key] = value;
                } else {
                    result[key] = value;
                }

                return result;
            },
            {}
        );

        // serialize the parameters
        query = "?" + $.param(params);

        _.forIn(arrayParameterCollection, function (arr, key) {
            // convert the array values into query parameters that will result in:
            //   ?key=value1&key=value2&...
            _.each(arr, function (item) {
                if (query === "?") {
                    query += key + "=" + item;
                } else {
                    query += "&" + key + "=" + item;
                }
            });
        });
    }
    return query;
};

/**
 * Call the api.
 * @returns {promise} a jquery promise object that will resolve with the response from
 *                    the server. If a problem occurs with the request, catch()
 *                    handlers that are registered with the promise can expect the
 *                    following arguments to their handler:
 *
 *                        .catch(function({object} error) { });
 *
 *                    - {object} error An object that contains data about the speicifc
 *                                     error that occurred in the request. This object
 *                                     will contain the following attributes:
 *
 *                                       - type {string}
 *                                           The type of error that occurred. Possible
 *                                           values may inclue "timeout", "abort",
 *                                           "parsererror", HTTP status codes.
 *                                       - details {object} (optional)
 *                                           For certain types of errors, an object
 *                                           may be included in the error that will
 *                                           contain more information about the error
 *                                           that occurred.
 */
const call = function (method, path, data, parameters) {
    let url = apiRoot + path;

    if (path.indexOf("http") === 0) {
        // If the path argument is a full URL, then don't prefix it with the api root
        url = path;
    }

    if (apiVersion) {
        url = url.replace(/\/api\/v[0-9]*\//, "/api/" + apiVersion + "/");
    }

    const options = {
        type: method,
        url: url + queryStr(parameters),
        contentType: "application/json; charset=UTF-8",
        dataType: "text",
        processData: false,
        timeout: REQUEST_TIMEOUT,
    };

    /** Temporary workaround for IE 11 caching issues. TODO: Remove on completion of UI-1582 **/
    if (navigator.userAgent.indexOf("Trident") > 0) {
        options.cache = false;
    }

    if (data) {
        options.data = JSON.stringify(data);
    }

    options.headers = {
        [HEADER_CLIENT_PATH]: window.location.hash,
        [HEADER_GENESYS_APP]: getGenesysAppHeaderValue(),
    };

    if (oAuthBearerToken) {
        options.headers.Authorization = "bearer " + oAuthBearerToken;
    }

    const jqXHR = $.ajax(options);

    const promise = Promise.cast(jqXHR)
        .cancellable() // make sure Promise is cancellable
        .then(function (response) {
            if (response) {
                return Promise.resolve(JSON.parse(response));
            } else {
                return Promise.resolve();
            }
        })
        .catch(function (error) {
            if (error.status === 401) {
                embeddingService.invalidateSession();
            } else if (error.name === "CancellationError") {
                LOG.trace("apiCore promise cancelled", error);
                jqXHR.abort();
                return Promise.reject(error);
            } else {
                return Promise.reject(new HttpError(error));
            }
        });

    requestTracker.addRequest(promise);

    return promise;
};

const restMethods = {
    /**
     * Gets a resource at the provided path.
     * @param {string} path the path to the object to get
     * @param {object} parameters a key-value object containing any query parameters
     *   to use in the request
     * @return {promise} a promise that resolves with the response from the server
     */
    get: function (path, parameters) {
        return call("GET", path, undefined, parameters);
    },

    /**
     * Patches a resource at the provided path.
     * @param {string} path the path to the object to get
     * @param {object} data the data that should be sent to the server
     * @param {object} parameters a key-value object containing any query parameters
     *   to use in the request
     * @return {promise} a promise that resolves with the response from the server
     */
    patch: function (path, data, parameters) {
        return call("PATCH", path, data, parameters);
    },

    /**
     * Puts a resource at the provided path.
     * @param {string} path the path to the object to get
     * @param {object} data the data that should be sent to the server
     * @param {object} parameters a key-value object containing any query parameters
     *   to use in the request
     * @return {promise} a promise that resolves with the response from the server
     */
    put: function (path, data, parameters) {
        return call("PUT", path, data, parameters);
    },

    /**
     * Posts a resource at the provided path.
     * @param {string} path the path to the object to get
     * @param {object} data the data that should be sent to the server
     * @param {object} parameters a key-value object containing any query parameters
     *   to use in the request
     * @return {promise} a promise that resolves with the response from the server
     */
    post: function (path, data, parameters) {
        return call("POST", path, data, parameters);
    },

    /**
     * Deletes a resource at the provided path.
     * @param {string} path the path to the object to get
     * @param {object} parameters a key-value object containing any query parameters
     *   to use in the request
     * @return {promise} a promise that resolves with the response from the server
     */
    del: function (path, parameters) {
        return call("DELETE", path, undefined, parameters);
    },
};

/**
 * Api Core service. Handles basic ajax requests to our API as well
 * as parsing the error format returned by the API and providing
 * utilities for parsing those errors in specific service modules.
 * @exports services/apiCore
 */
const apiCore = _.assign(
    {
        /**
         * Sets the current OAuth Bearer token used for authentication
         * @param {string} token
         */
        setOAuthBearerToken: function (token) {
            oAuthBearerToken = token;
        },
    },
    restMethods
);

/**
 * Support multiple versions of the API. Version can be specified on a per request
 * basis by calling apiCore.<version>.<restMethod>(...), e.g. apiCore.v2.get(...).
 * Calling apiCore.<restMethod>(...) without a specified version will use the
 * version specified by the config.api.uri.
 */
const versions = ["v1", "v2"];
_.forEach(versions, function (version) {
    apiCore[version] = {};
    _.forEach(restMethods, function (method, key) {
        apiCore[version][key] = function () {
            apiVersion = version;
            const result = method.apply(this, arguments);
            apiVersion = null;
            return result;
        };
    });
});

export default apiCore;
